import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { VOYAGER } from '@carto/react-basemaps';
import { API_VERSIONS } from '@deck.gl/carto/typed';
import { FEATURE_SELECTION_MODES } from '@carto/react-core';
import { ViewState } from '@carto/react-redux';
import { SpatialFilter } from '@carto/react-ui';
import { Asset } from 'components/common/types/Asset.types';

interface LineSelection {
  spatialFilters: (SpatialFilter & { polygonId: string })[];
  featureSelectionMode: string;
  featureSelectionEnabled: boolean;
}

export const defaultLineSelection: LineSelection = {
  spatialFilters: [],
  featureSelectionMode: FEATURE_SELECTION_MODES.POLYGON,
  featureSelectionEnabled: false,
};

type LineSelections = {
  [lineId: string]: LineSelection;
};

type ClusterSelectionData = {
  latitude: number;
  longitude: number;
  address1: string;
  address2: string;
  assets: Asset[];
};

export type ClusterSelection = {
  furnitureCode: string;
  assets: Asset[];
};

export type ClusterSelectionMapData = ClusterSelectionData & { frameId: string };

export type SelectedLasso = {
  polygonId: string | null;
  anchor: number[] | null;
};

export interface CartoState {
  viewState: ViewState;
  basemap: string;
  credentials: {
    apiVersion: string;
    apiBaseUrl: string;
    accessToken: string;
  };
  googleApiKey: string;
  googleMapId: string;
  featuresReady: { [key: string]: boolean };
  lineSelections: LineSelections;
  selectedCluster: ClusterSelection;
  selectedLasso: SelectedLasso;
}

const initialState: CartoState = {
  viewState: {
    latitude: 0,
    longitude: 0,
    zoom: 5,
    pitch: 0,
    width: 0,
    height: 0,
    bearing: 0,
    dragRotate: false,
  },
  basemap: VOYAGER,
  credentials: {
    apiVersion: API_VERSIONS.V3,
    apiBaseUrl: '',
    accessToken: '',
  },
  googleApiKey: '',
  googleMapId: '',
  featuresReady: {},
  lineSelections: {},
  selectedCluster: {
    furnitureCode: '',
    assets: [],
  },
  selectedLasso: {
    anchor: null,
    polygonId: null,
  },
};

export const cartoSlice = createSlice({
  name: 'customCarto',
  initialState,
  reducers: {
    setViewState: (state, action: PayloadAction<Partial<ViewState>>) => {
      state.viewState = { ...state.viewState, ...action.payload };
    },

    setBasemap: (state, action: PayloadAction<string>) => {
      state.basemap = action.payload;
    },

    setCredentials: (state, action: PayloadAction<Partial<CartoState['credentials']>>) => {
      state.credentials = { ...state.credentials, ...action.payload };
    },

    setGoogleApiKey: (state, action: PayloadAction<string>) => {
      state.googleApiKey = action.payload;
    },

    setGoogleMapId: (state, action: PayloadAction<string>) => {
      state.googleMapId = action.payload;
    },

    setFeatureReady: (state, action: PayloadAction<{ key: string; ready: boolean }>) => {
      const { key, ready } = action.payload;
      state.featuresReady[key] = ready;
    },

    setFeaturesReady: (state, action: PayloadAction<{ [key: string]: boolean }>) => {
      state.featuresReady = { ...state.featuresReady, ...action.payload };
    },

    removeFeatureReady: (state, action: PayloadAction<string>) => {
      const key = action.payload;
      delete state.featuresReady[key];
    },

    clearAllFeaturesReady: (state) => {
      state.featuresReady = {};
    },

    setFeatureSelectionEnabledForLine: (state, action: PayloadAction<{ lineId: string; enabled: boolean }>) => {
      const { lineId, enabled } = action.payload;

      if (!state.lineSelections[lineId]) {
        state.lineSelections[lineId] = { ...defaultLineSelection };
      }
      state.lineSelections[lineId].featureSelectionEnabled = enabled;
    },

    setFeatureSelectionModeForLine: (state, action: PayloadAction<{ lineId: string; mode: string }>) => {
      const { lineId, mode } = action.payload;

      if (!state.lineSelections[lineId]) {
        state.lineSelections[lineId] = { ...defaultLineSelection };
      }
      state.lineSelections[lineId].featureSelectionMode = mode;
    },

    loadPolygons: (state, action: PayloadAction<Record<string, SpatialFilter[]>>) => {
      Object.entries(action.payload).forEach(([lineId, spatialFilters]) => {
        state.lineSelections[lineId] = { ...defaultLineSelection, spatialFilters };
      });
    },

    addSpatialFilterForLine: (state, action: PayloadAction<{ lineId: string; spatialFilter: SpatialFilter }>) => {
      const { lineId, spatialFilter } = action.payload;

      if (!state.lineSelections[lineId]) {
        state.lineSelections[lineId] = { ...defaultLineSelection };
      }
      state.lineSelections[lineId].spatialFilters.push(spatialFilter);
    },

    removeSpatialFilterForLine: (state, action: PayloadAction<{ lineId: string }>) => {
      const { lineId } = action.payload;

      if (state.lineSelections[lineId]) {
        state.lineSelections[lineId].spatialFilters = [];
      }
    },

    initializeLineSelection: (state, action: PayloadAction<{ lineId: string; selection?: Partial<LineSelection> }>) => {
      const { lineId, selection } = action.payload;

      state.lineSelections[lineId] = {
        ...defaultLineSelection,
        ...selection,
      };
    },

    removeLineSelection: (state, action: PayloadAction<string>) => {
      const lineId = action.payload;
      delete state.lineSelections[lineId];
    },

    clearAllLineSelections: (state) => {
      state.lineSelections = {};
    },

    setMultipleLineSelections: (state, action: PayloadAction<LineSelections>) => {
      state.lineSelections = { ...state.lineSelections, ...action.payload };
    },

    enableSelectionForAllLines: (state, action: PayloadAction<boolean>) => {
      const enabled = action.payload;
      Object.keys(state.lineSelections).forEach((lineId) => {
        state.lineSelections[lineId].featureSelectionEnabled = enabled;
      });
    },

    setModeForAllLines: (state, action: PayloadAction<string>) => {
      const mode = action.payload;
      Object.keys(state.lineSelections).forEach((lineId) => {
        state.lineSelections[lineId].featureSelectionMode = mode;
      });
    },

    clearAllSpatialFilters: (state) => {
      Object.keys(state.lineSelections).forEach((lineId) => {
        state.lineSelections[lineId].spatialFilters = [];
      });
    },

    removeLineSpatialFilters: (state, action: PayloadAction<string>) => {
      delete state.lineSelections[action.payload];
    },

    addClusterSelection: (state, action: PayloadAction<ClusterSelection>) => {
      state.selectedCluster = action.payload;
    },

    clearClusterSelection: (state) => {
      state.selectedCluster = {
        furnitureCode: '',
        assets: [],
      };
    },

    removeLassoSelection: (state, action: PayloadAction<{ lineId: string; polygonId: string }>) => {
      const spatialFilters = state.lineSelections?.[action.payload.lineId]?.spatialFilters;
      const foundSpatialFilterIndex = spatialFilters.findIndex(
        (spatialFilterItem: SpatialFilter) => spatialFilterItem.polygonId === action.payload.polygonId,
      );

      if (foundSpatialFilterIndex > -1) {
        state.selectedCluster = {
          furnitureCode: '',
          assets: [],
        };
        state.selectedLasso = {
          anchor: null,
          polygonId: null,
        };

        spatialFilters.splice(foundSpatialFilterIndex, 1);
      }
    },

    removeNewLassoSelection: (state, action: PayloadAction<string>) => {
      const spatialFilters = state.lineSelections?.[action.payload]?.spatialFilters;

      state.selectedCluster = {
        furnitureCode: '',
        assets: [],
      };
      state.selectedLasso = {
        anchor: null,
        polygonId: null,
      };

      spatialFilters.splice(spatialFilters.length - 1, 1);
    },

    addLassoSelection: (state, action: PayloadAction<{ idx: number; anchor: number[]; polygonId: string | null }>) => {
      state.selectedLasso = action.payload;
    },

    assignPolygonId: (state, action: PayloadAction<{ lineId: string; polygonId: string }>) => {
      state.selectedLasso.polygonId = action.payload.polygonId;
      const lastElementIdx = state.lineSelections[action.payload.lineId].spatialFilters.length - 1;
      const lastElement = state.lineSelections[action.payload.lineId].spatialFilters[lastElementIdx];

      if (!lastElement.polygonId) {
        lastElement.polygonId = action.payload.polygonId;
      }
    },

    clearLassoSelection: (state) => {
      state.selectedLasso = {
        anchor: null,
        polygonId: null,
      };
    },
  },
});

export const {
  setViewState,
  setBasemap,
  setCredentials,
  setGoogleApiKey,
  setGoogleMapId,
  setFeatureReady,
  setFeaturesReady,
  removeFeatureReady,
  clearAllFeaturesReady,
  setFeatureSelectionEnabledForLine,
  setFeatureSelectionModeForLine,
  loadPolygons,
  addSpatialFilterForLine,
  removeSpatialFilterForLine,
  removeNewLassoSelection,
  initializeLineSelection,
  removeLineSelection,
  clearAllLineSelections,
  setMultipleLineSelections,
  enableSelectionForAllLines,
  setModeForAllLines,
  clearAllSpatialFilters,
  addClusterSelection,
  removeLineSpatialFilters,
  clearClusterSelection,
  addLassoSelection,
  removeLassoSelection,
  clearLassoSelection,
  assignPolygonId,
} = cartoSlice.actions;

export default cartoSlice.reducer;
export type { LineSelection, LineSelections };
